import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import './CookieConsentBanner.css';

/**
 * Cookie Consent Banner Component
 * 
 * Displays a cookie consent banner on first visit.
 * Allows users to accept, reject, or customize cookie preferences.
 * Stores preferences in localStorage.
 */
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    functional: false,
  });
  const history = useHistory();

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(cookieConsent);
        setPreferences(savedPreferences);
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowPreferences(false);
    
    // Apply preferences (you can add logic here to enable/disable analytics)
    if (prefs.analytics) {
      // Enable analytics tracking
      console.log('Analytics cookies enabled');
    } else {
      // Disable analytics tracking
      console.log('Analytics cookies disabled');
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
    };
    savePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      functional: false,
    };
    savePreferences(onlyEssential);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const handleTogglePreference = (key) => {
    if (key === 'essential') {
      // Essential cookies cannot be disabled
      return;
    }
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleViewCookiePolicy = () => {
    history.push('/cookie-policy');
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-banner-overlay">
      <div className="cookie-banner">
        {!showPreferences ? (
          <>
            <div className="cookie-banner-content">
              <div className="cookie-banner-header">
                <h3>🍪 Cookie Preferences</h3>
              </div>
              <p>
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                By clicking "Accept All", you consent to our use of cookies. You can also choose to customize your preferences.
              </p>
              <div className="cookie-banner-links">
                <a href="/cookie-policy" onClick={(e) => {
                  e.preventDefault();
                  handleViewCookiePolicy();
                }}>
                  Learn more in our Cookie Policy
                </a>
              </div>
            </div>
            <div className="cookie-banner-actions">
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={() => setShowPreferences(true)}
              >
                Customize
              </button>
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-banner-content">
              <div className="cookie-banner-header">
                <h3>Customize Cookie Preferences</h3>
                <button 
                  className="cookie-close-btn"
                  onClick={() => setShowPreferences(false)}
                  aria-label="Close preferences"
                >
                  ×
                </button>
              </div>
              
              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label>
                    <strong>Essential Cookies</strong>
                    <span className="cookie-required">Required</span>
                  </label>
                </div>
                <p className="cookie-preference-description">
                  These cookies are necessary for the website to function and cannot be switched off. 
                  They include authentication, security, and session management.
                </p>
                <div className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.essential}
                    disabled
                    readOnly
                  />
                  <span className="cookie-toggle-label">Always Active</span>
                </div>
              </div>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label>
                    <strong>Analytics Cookies</strong>
                  </label>
                </div>
                <p className="cookie-preference-description">
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously (e.g., Firebase Analytics).
                </p>
                <div className="cookie-toggle">
                  <label className="cookie-switch">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => handleTogglePreference('analytics')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                  <span className="cookie-toggle-label">
                    {preferences.analytics ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label>
                    <strong>Functional Cookies</strong>
                  </label>
                </div>
                <p className="cookie-preference-description">
                  These cookies enable enhanced functionality and personalization, such as remembering 
                  your preferences and settings.
                </p>
                <div className="cookie-toggle">
                  <label className="cookie-switch">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => handleTogglePreference('functional')}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                  <span className="cookie-toggle-label">
                    {preferences.functional ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="cookie-banner-links">
                <a href="/cookie-policy" onClick={(e) => {
                  e.preventDefault();
                  handleViewCookiePolicy();
                }}>
                  Learn more in our Cookie Policy
                </a>
              </div>
            </div>
            <div className="cookie-banner-actions">
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={() => setShowPreferences(false)}
              >
                Cancel
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;

